import React from 'react';
import { useTranslation } from 'react-i18next';
import { STORES } from '../../config/stores';
import './Button.css';
import './AppDownloadLinks.css';

interface AppDownloadLinksProps {
  /** `onBrand` is for the red call-to-action band, where links must be white. */
  tone?: 'default' | 'onBrand';
}

/**
 * Store buttons for the iPhone and Android apps.
 *
 * A platform with a public store URL in `config/stores.ts` gets a real link. One
 * without (both, while the apps are invite-only betas) gets an honest "coming soon"
 * label plus an email link to ask to join the beta — never a button that does nothing.
 */
export const AppDownloadLinks: React.FC<AppDownloadLinksProps> = ({ tone = 'default' }) => {
  const { t } = useTranslation();
  const platforms = [
    { id: 'ios', url: STORES.iosUrl, label: t('download.iphone'), soon: t('download.iphoneSoon') },
    { id: 'android', url: STORES.androidUrl, label: t('download.android'), soon: t('download.androidSoon') },
  ];
  const betaHref = `mailto:${STORES.betaEmail}?subject=${encodeURIComponent(t('download.betaSubject'))}`;
  const anyComingSoon = platforms.some((p) => !p.url);

  return (
    <div className={`app-download app-download-${tone}`}>
      <div className="app-download-buttons">
        {platforms.map((p, i) =>
          p.url ? (
            <a
              key={p.id}
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`btn btn-large ${i === 0 ? 'btn-primary' : 'btn-secondary'}`}
            >
              {p.label}
            </a>
          ) : (
            <span key={p.id} className="app-download-soon" aria-disabled="true">
              {p.soon}
            </span>
          )
        )}
      </div>
      {anyComingSoon && (
        <p className="app-download-beta">
          {t('download.betaPrompt')}{' '}
          <a href={betaHref}>{t('download.betaLink')}</a>
        </p>
      )}
    </div>
  );
};
